import { highlightSqlSegments, type SqlSegmentKind } from "../lib/sql-format";

/**
 * Four hues, one per question a statement is read for — see `SqlSegmentKind`.
 *
 * Every one is a `*-subtle-foreground` token rather than the base hue: those are
 * the readable members of each colour, and they are the only ones that clear
 * 4.5:1 on a card at 11px in both themes. The base `--warning` and `--success`
 * do not, which is the whole reason this map does not name them.
 *
 * Colour is never the only signal. The keywords arrive upper case and carry
 * weight as well, the identifiers keep their backticks, and a placeholder is a
 * `?` whatever colour it is drawn in.
 */
const SQL_SEGMENT_CLASS: Record<SqlSegmentKind, string> = {
  keyword: "font-medium text-info-subtle-foreground",
  identifier: "text-violet-subtle-foreground",
  call: "text-warning-subtle-foreground",
  placeholder: "text-success-subtle-foreground",
  plain: "",
};

export interface SqlLineProps {
  /** One line of normalized SQL. */
  line: string;
  /** Sizing and truncation belong to the surface, not to the colouring. */
  className?: string;
  title?: string;
}

/**
 * One line of normalized SQL, coloured by segment.
 *
 * The tables draw a single truncated line and the drawer draws a clause per
 * line, but both read the same palette from here: a colour that meant "table" in
 * a row cannot come to mean something else in the drawer that row opens.
 */
export function SqlLine({ line, className, title }: SqlLineProps) {
  return (
    <span className={className} title={title}>
      {highlightSqlSegments(line).map((segment, index) => (
        <span key={index} className={SQL_SEGMENT_CLASS[segment.kind]}>
          {segment.text}
        </span>
      ))}
    </span>
  );
}
